import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { jsPDF } from 'jspdf';
import autoTable, { CellHookData, Styles, UserOptions } from 'jspdf-autotable';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/app-config.loader';
import { AnagraficheService } from 'src/app/core/anagrafiche.service';
import { RegistriConstants, RegistriService } from 'src/app/core/registri.service';
import { Classifica } from 'src/app/shared/models/classifica.model';
import { Documento } from 'src/app/shared/models/documento.model';
import { Qualifica } from 'src/app/shared/models/qualifica.model';
import { RegistriRequestFiltro, RegistroCategoria, RegistroGenerico, RegistroProtocollo, RegistroRiproduzioni } from 'src/app/shared/models/registriRequest.model';
import { RegistroColumn } from 'src/app/shared/models/registro-column.model';
import { declassificatoRegex, distruttoRegex, versatoRegex } from '../../shared/pipes/highlight-distrutto.pipe';
import { DateRangeChangedEvent, SelectedRegister } from './registri.model';


export enum RegisterTypes {
  NC = 'NC',
  E = 'E',
  R = 'R',
  RRS = 'RRS',
  SS = 'SS'
}

@Component({
  selector: 'app-registri',
  templateUrl: './registri.component.html',
  styleUrls: ['./registri.component.scss']
})
export class RegistriComponent implements OnInit, OnDestroy {
  // selectedRegister: TipoRegistro;
  // selectedQualifica: QualificaRegistro;

  private subscriber$ = new Subject<void>();

  selectedRegister: SelectedRegister | undefined;
  validRegisterSelection: boolean | undefined;
  dateStart: Date = new Date();
  dateEnd: Date = new Date();
  arrivi: Documento[] | undefined;
  partenze: Documento[] | undefined;
  registro: Documento[] | undefined;
  registroProtocollo: RegistroProtocollo[] | undefined;
  registroRiproduzioni: RegistroRiproduzioni[] | undefined;
  registroToPrint: RegistroGenerico[] | undefined;

  colsToPrint: RegistroColumn[] | undefined;
  additionalPDFStyles: UserOptions = {};
  public formIsNotValid = true;
  classifiche: Classifica[] = [];
  qualifiche: Qualifica[] = [];
  elencoRegistri: RegistroCategoria[] | undefined;
  public title = '';

  private fileName: string | undefined;
  private rowHeight = 0;

  constructor(private appConfig: AppConfig, private registriService: RegistriService, private anagraficheService: AnagraficheService) { }

  ngOnInit(): void {
    this.anagraficheService.classifiche.getAll().pipe(takeUntil(this.subscriber$)).subscribe(c => this.classifiche = c);
    this.anagraficheService.qualifiche.getAll().pipe(takeUntil(this.subscriber$)).subscribe(q => this.qualifiche = q);
    this.validRegisterSelection = false;

    this.registriService.categorie().subscribe(registri => {
      this.elencoRegistri = registri;
    });
  }

  ngOnDestroy(): void {
    this.subscriber$.next();
    this.subscriber$.complete();
  }

  generaRegistro(): void {
    const registroTipoId = this.selectedRegister?.tipo?.registroTipoId;
    if (!registroTipoId) return;

    const categoria = this.selectedRegister?.categoria?.registroCategoriaId;
    if (!categoria) return;

    this.additionalPDFStyles.startY = 20;
    this.additionalPDFStyles.margin = { top: this.additionalPDFStyles.startY, bottom: 30 };
    switch (categoria) {
      case 'PR':
        this.generaRegistriProtocollo(registroTipoId);
        this.colsToPrint = this.registriService.colsProtocollo;
        break;
      case 'RI':
        this.generaRegistroRiproduzioni(registroTipoId);
        this.colsToPrint = this.registriService.colsRiproduzioni;
        this.additionalPDFStyles.styles = { fontSize: RegistriConstants.FONT_SMALL };
        break;
      case 'UC':
        this.generaRegistroUnicoControllo(registroTipoId);
        this.colsToPrint = this.registriService.colsUnicoControllo;
        this.additionalPDFStyles.startY = 30;
        this.additionalPDFStyles.margin = { top: 30, bottom: 30 };
        break;
      case 'CA':
        this.generaRegistroCarico(registroTipoId);
        this.colsToPrint = this.registriService.colsCarico;
        this.additionalPDFStyles.margin = { top: 20, bottom: 20 };
        break;
      case 'DI':
        this.generaRegistroDistruzioni(registroTipoId);
        this.colsToPrint = this.registriService.colsDistruzioni;
        break;
      case 'CI':
        this.generaRegistroAlleagatiCIS(registroTipoId);
        this.colsToPrint = this.registriService.colsAllegatiCIS;
        break;
    }

    this.additionalPDFStyles.columnStyles = this.getPDFColumnStyles(this.colsToPrint);
  }

  private generaRegistriProtocollo(registroTipoId: string) {
    this.registriService.protocollo({ registroTipoId, filtro: RegistriRequestFiltro.byData, dataInizio: this.dateStart, dataFine: this.dateEnd }).subscribe(response => {
      this.registroToPrint = response;
      const anno = this.dateStart?.getFullYear();
      this.title = `${this.appConfig.text.registri.protocollo.replace('<ANNO>', anno?.toString())} ${this.selectedRegister?.tipo?.nome}`;
      this.fileName = this.selectedRegister?.categoria?.nome ? `RegistroProtocollo_${this.selectedRegister.categoria.nome.replace(/ /g, '_')}.pdf` : '';
    });
  }

  private generaRegistroRiproduzioni(registroTipoId: string) {
    this.registriService.riproduzioni({ registroTipoId, filtro: RegistriRequestFiltro.byData, dataInizio: this.dateStart, dataFine: this.dateEnd }).subscribe(response => {
      this.registroToPrint = response;
      this.title = `${this.appConfig.text.registri.riproduzioni}`;
      this.fileName = `RegistroRiproduzioni.pdf`;
    });
  }

  private generaRegistroUnicoControllo(registroTipoId: string) {
    this.registriService.unicoControllo({ registroTipoId, filtro: RegistriRequestFiltro.byData, dataInizio: this.dateStart, dataFine: this.dateEnd }).subscribe(response => {
      this.registroToPrint = response;
      this.title = `${this.appConfig.text.registri.unicoControllo}`;
      this.fileName = `RegistroUnicoControllo.pdf`;
    });
  }

  private generaRegistroCarico(registroTipoId: string) {
    const qualificaName = this.getNomeQualificaFromTipoCarico(registroTipoId);
    this.registriService.carico({ registroTipoId, filtro: RegistriRequestFiltro.byData, dataInizio: this.dateStart, dataFine: this.dateEnd }).subscribe(response => {
      this.registroToPrint = response;
      this.title = `${this.appConfig.text.registri.carico.replace('<QUALIFICA>', qualificaName)}`;
      this.fileName = `RegistroCarico.pdf`;
    });
  }

  private getNomeQualificaFromTipoCarico(registroTipoId: string) {
    const qualificaId = registroTipoId.split('_')[1];
    const qualifica = this.qualifiche.find(c => c.qualificaId.toString() === qualificaId);
    const qualificaName = qualifica ? qualifica.nomeQualifica.toUpperCase() : '';
    return qualificaName;
  }

  private getNomeClassificaFromTipoCarico(registroTipoId: string) {
    const classificaId = registroTipoId.split('_')[2];
    const classifica = this.classifiche.filter(c => c.classificaId).find(c => c.classificaId?.toString() === classificaId);
    const classificaName = classifica ? classifica.nomeClassifica.toUpperCase() : '';
    return classificaName;
  }

  private generaRegistroDistruzioni(registroTipoId: string) {
    this.registriService.distruzioni({ registroTipoId, filtro: RegistriRequestFiltro.byData, dataInizio: this.dateStart, dataFine: this.dateEnd }).subscribe(response => {
      this.registroToPrint = response;
      this.title = `${this.appConfig.text.registri.distruzioni}`;
      this.fileName = `RegistroDistruzioni.pdf`;
    });
  }

  private generaRegistroAlleagatiCIS(registroTipoId: string) {
    this.registriService.allegatiCIS({ registroTipoId, filtro: RegistriRequestFiltro.byData, dataInizio: this.dateStart, dataFine: this.dateEnd }).subscribe(response => {
      this.registroToPrint = response;
      this.title = `${this.appConfig.text.registri.allegatiCIS}`;
      this.fileName = `RegistroCIS.pdf`;
    });
  }

  private getPDFColumnStyles(cols: RegistroColumn[] | undefined): { [key: string]: Partial<Styles>; } | undefined {
    if (!cols) return;

    const columnStyles: { [key: string]: Partial<Styles>; } = {};
cols.forEach((col, index) => {
  let width = col.width ? Math.ceil(parseInt(col.width, 10) / 3.78) : undefined;

  columnStyles[index] = { cellWidth: width, halign: col.align, fontSize: col.fontSize };
});

    return columnStyles;
  }

  public GeneraPDFRegistro(): void {

    setTimeout(() => {
      const doc = new jsPDF('l', 'mm', 'a3');
      const totalPagesExp = '{total_pages_count_string}';
      autoTable(doc, {
        html: '#pdfTablePrime table',
        theme: 'grid',
        didDrawPage: () => {
          if (this.selectedRegister?.categoria) {
            // Header
            const dataFine = 'Roma, ' + new Date().toLocaleDateString('it-IT');
            doc.text(dataFine, 15, 10);
            //stampa il logo, isottotitoli e il titolo solo sulla prima pagina
            const page = doc.getCurrentPageInfo().pageNumber;
            if (page == 1) {
              const imageSize = 20;
              const marginTop = 10;
              const marginLeft = (doc.internal.pageSize.width - imageSize) / 2;
              // Aggiungi l'immagine
              doc.addImage(
                '../../../assets/images/italia-emblema.png',
                'PNG',
                marginLeft,
                marginTop,
                imageSize,
                imageSize
              );
              doc.line(170, 54, 250, 54);
              doc.setFont('Helvetica', 'italic');
              doc.text('(TIMBRO LINEARE D\'UFFICIO)', doc.internal.pageSize.width / 2, 60, { align: 'center' });
              doc.text('ORGANO PERIFERICO DI SICUREZZA', doc.internal.pageSize.width / 2, 70, { align: 'center' });
              //imposta il font a grassetto
              doc.setFont('Helvetica', 'bold');

              if (this.selectedRegister.categoria.nome === 'Unico Controllo') {
                const strArray: string[] = doc.splitTextToSize(this.title, 300) as string[];
                doc.text(strArray, doc.internal.pageSize.width / 2, 80, { align: 'center' });
              } else {
                doc.text(this.title, doc.internal.pageSize.width / 2, 80, { align: 'center' });
              }

              // Aggiunge la sottolineatura
              if (this.selectedRegister.categoria.nome === 'Distruzioni') {
                doc.line(185, 81, 223, 81);
              }
            }

            // Ripristina il font normale
            doc.setFont('Helvetica', 'normal');

            // Footer
            let str = `Pagina ${doc.getNumberOfPages()}`;
            if (typeof doc.putTotalPages === 'function') {
              str = str + ' di  ' + totalPagesExp;
            }
            doc.setFontSize(10);

            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            this.rowHeight = (pageHeight - RegistriConstants.TOTAL_HEADER_FOOTER) / RegistriConstants.ROWS_NUMBER_PROTOCOLLO;
            doc.text(str, 10, pageHeight - 10);

          }
        },
        didParseCell: (cellData) => {
          if (typeof cellData.cell === 'undefined') {
            return;
          }

          this.setCellFontSize(cellData);

          if (distruttoRegex.test(cellData.cell.text[0]) || declassificatoRegex.test(cellData.cell.text[0]) || versatoRegex.test(cellData.cell.text[0])) {
            cellData.cell.styles.textColor = 'red';
          }
        },
        didDrawCell: (cellData) => {
          if (!this.selectedRegister) return;

          if (this.selectedRegister.categoria.nome === 'Riproduzioni') {
            if (cellData.column.index === this.registriService.colsRiproduzioni.length - 1 && cellData.cell.text[0]?.indexOf('Distrutto') > -1) {
              cellData.cell.styles.textColor = 'red';
              //barra la riga per la riproduzione distrutta
              this.drawRedLine(doc, cellData);
            }
          } else if (this.selectedRegister.categoria.nome === 'Protocollo' || this.selectedRegister.categoria.nome === 'Unico Controllo') {
            if (cellData.column.index === (this.colsToPrint?.length ?? 0) - 1 && cellData.cell.text[0]?.indexOf('Protocollo annullato') > -1) {
              this.drawRedLine(doc, cellData);
            }
          }
        },
        tableLineWidth: 0.7,
        headStyles: {
          fontStyle: 'bold',
          lineWidth: 0.1,
          textColor: [0, 0, 0],
          fillColor: [255, 255, 255],
          fontSize: RegistriConstants.FONT_HEADER,
          halign: 'center'
        },
        rowPageBreak: 'avoid',
        startY: this.additionalPDFStyles.startY ? this.additionalPDFStyles.startY + 70 : 0,
        styles: this.additionalPDFStyles.styles,
        columnStyles: this.additionalPDFStyles.columnStyles,
        margin: this.additionalPDFStyles.margin
      });

      let finalY = (doc as any).lastAutoTable.finalY + 10;
      if (this.selectedRegister?.categoria?.nome === 'Carico' && new Date().getFullYear() > this.dateEnd.getFullYear()) {
        const registroTipoId = this.selectedRegister?.tipo?.registroTipoId;
        const chiusuraCaricoRiga1 = 'Si dichiara che alla data del ' + formatDate(new Date(this.dateEnd.getFullYear(), 11, 31), 'dd/MM/yyyy', 'it-IT') +
          ' il carico e la completezza dei documenti o messaggi elencati nel presente registro di carico "' +
          (this.getNomeQualificaFromTipoCarico(registroTipoId) !== 'NAZIONALE' ? this.getNomeQualificaFromTipoCarico(registroTipoId) + ' ' : '') +
          this.getNomeClassificaFromTipoCarico(registroTipoId) + '"';
        const chiusuraCaricoRiga2 = 'corrisponde esattamente ai documenti e ai messaggi per i quali questa organizzazione di sicurezza è responsabile';

        doc.setTextColor(255, 0, 0);
        doc.setFontSize(15);
        doc.text(chiusuraCaricoRiga1, 15, finalY);
        finalY = finalY + 5;
        doc.text(chiusuraCaricoRiga2, 15, finalY);
        finalY = finalY + 10;
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        const firmaFunzionario = 'Il funzionario di controllo';
        doc.text(firmaFunzionario, 325, finalY);
        doc.text('.   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .   .', 300, finalY + 15);
        doc.setFont('helvetica', 'normal');
      }
      let firma = '';
      doc.setFontSize(10);
      if (this.selectedRegister?.categoria.nome === 'Distruzioni') {
        doc.line(280, finalY + 20, 380, finalY + 20);
        firma = '(Firma del Funzionario/Ufficiale di controllo, da \napporre dopo la registrazione dell\'ultimo verbale \ndi distruzione dell\'anno solare di riferimento)';
        doc.text(firma, 290, finalY + 25);
      }
      else {
        firma = 'Sigla Capo Segreteria Speciale    .   .   .   .   .   .    .   .   .   .   .   .';
        doc.text(firma, 300, finalY + 15);
      }

      if (typeof doc.putTotalPages === 'function') {
        doc.putTotalPages(totalPagesExp);
      }
      doc.save(this.fileName);
    }, 100);
  }


  public registerSelected(event: SelectedRegister): void {
    this.selectedRegister = event;
    this.checkFormIsValid();
    this.registroToPrint = [];
  }

  public dateRangeChanged(event: DateRangeChangedEvent): void {
    this.dateStart = event.dateStart;
    this.dateEnd = event.dateEnd;
    this.checkFormIsValid();
  }

  checkFormIsValid(): void {
    if (this.selectedRegister && this.dateStart && this.dateEnd && this.dateStart.getFullYear() > 2000 && this.dateStart.getTime() < this.dateEnd.getTime()) {
      this.formIsNotValid = false;
    } else {
      this.formIsNotValid = true;
    }
  }

  private drawRedLine(doc: jsPDF, cellData: CellHookData): void {
    doc.setDrawColor(255, 0, 0);
    doc.setLineWidth(0.5);
    const xEnd = cellData.cell.x + cellData.cell.width;
    const xStart = doc.internal.pageSize.getWidth() - xEnd;
    const quarterHeight = cellData.cell.height / 4;
    const yStart = cellData.cell.y + quarterHeight;
    const yEnd = yStart + (2 * quarterHeight);
    doc.line(xStart, yStart, xEnd, yEnd);
  }

  private setCellFontSize(cellData: CellHookData) {
    if (cellData.row.section === 'body') {
      const defaultSize = this.additionalPDFStyles.styles?.fontSize ?? RegistriConstants.FONT_DEFAULT;
      cellData.cell.styles.fontSize = this.additionalPDFStyles.columnStyles?.[cellData.column.index]?.fontSize ?? defaultSize;
      switch (this.selectedRegister?.categoria?.nome) {
        case 'Protocollo':
          if (cellData.column.index === 0) {
            cellData.row.height = this.rowHeight;
          }
          // colonna posizioni
          else if (cellData.column.index === 6 && cellData.cell.text.some(text => text.length >= RegistriConstants.MAX_CHAR_POSIZIONI)) {
            cellData.cell.styles.fontSize = RegistriConstants.FONT_XSMALL;
          }
          // colonna annotazioni
          else if (cellData.column.index === 7 && cellData.cell.text[0].length >= RegistriConstants.MAX_CHAR_ANNOTAZIONI) {
            cellData.cell.styles.fontSize =
              cellData.cell.text[0].length >= RegistriConstants.MAX_CHAR_ANNOTAZIONISMALL
                ? RegistriConstants.FONT_XSMALL
                : RegistriConstants.FONT_SMALL;
          }
          break;
        case 'Riproduzioni':
          // colonna annotazioni
          if (cellData.column.index === this.registriService.colsRiproduzioni.length - 1 && cellData.cell.text[0].length >= RegistriConstants.MAX_CHAR_ANNOTAZIONISMALL) {
            cellData.cell.styles.fontSize = RegistriConstants.FONT_XSMALL;
          }
          break;
              case 'Allegati CIS':
      if (
        cellData.column.index === 14 &&
        cellData.cell.text?.[0]?.length >= RegistriConstants.MAX_CHAR_ANNOTAZIONI
      ) {
        cellData.cell.styles.fontSize =
          cellData.cell.text[0].length >= RegistriConstants.MAX_CHAR_ANNOTAZIONISMALL
            ? RegistriConstants.FONT_XSMALL
            : RegistriConstants.FONT_SMALL;
      }
      break;
      }
    }
    else if (cellData.row.section === 'head' && cellData.cell.text[0].length >= RegistriConstants.MAX_CHAR_HEADER) {
      cellData.cell.styles.fontSize = RegistriConstants.FONT_HEADER_SMALL;
    }
  }
}
